import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import Workflow from '../components/Workflow';
import ScreenshotGallery from '../components/ScreenshotGallery';
import TestimonialBlock from '../components/TestimonialBlock';

const HEALTHCARE_SHOTS = [
  { src: '/images/nhs/nhs-portfolio-tracker.jpg', caption: 'Multi-Trust portfolio tracker with capital, lifecycle and estates rollup.' },
  { src: '/images/nhs/nhs-pmo-command-centre.jpg', caption: 'PMO command centre — every Trust, every project, every stage.' },
  { src: '/images/nhs/nhs-project-riba-stages.jpg', caption: 'RIBA-aligned project stages with governance evidence at each gate.' },
  { src: '/images/nhs/nhs-board-pack.jpg', caption: 'AI-generated board pack in plain English — assembled from live data.' },
  { src: '/images/nhs/nhs-governance-reports.jpg', caption: 'Governance reports composed from the day\u2019s work — no manual re-typing.' },
  { src: '/images/nhs/nhs-compliance-rails.jpg', caption: 'Compliance rails for HTM/SHTM, IPC, safety and statutory obligations.' },
  { src: '/images/nhs/nhs-ipc-dashboard.jpg', caption: 'Infection prevention & control dashboard — condition, projects, evidence.' },
  { src: '/images/healthcare/hc-handback-readiness.jpg', caption: 'Progressive handback readiness view for PFI-adjacent contracts.' },
];

const COUNTRIES = [
  { name: 'NHS England', status: 'Available now',
    body: 'A dedicated Videra environment designed around project and lifecycle delivery within NHS England.',
    cta: { label: 'Try NHS England — 24 days', href: 'https://demo.videra.app/explore' } },
  { name: 'NHS Scotland', status: 'Available now',
    body: 'A dedicated Videra environment designed around Scottish healthcare project and lifecycle delivery requirements.',
    cta: { label: 'Try NHS Scotland — 24 days', href: 'https://demo.videra.app/explore' } },
  { name: 'NHS Wales', status: 'In production',
    body: 'A dedicated Welsh healthcare environment is currently being developed.',
    cta: { label: 'Register interest', to: '/contact' } },
  { name: 'Health & Social Care Northern Ireland', status: 'In production',
    body: 'A dedicated environment for Health & Social Care Northern Ireland is currently being developed.',
    cta: { label: 'Register interest', to: '/contact' } },
];

const V2VideraHealthcare = () => {
  useSEO({
    title: 'Videra Healthcare | NHS project & lifecycle platform',
    description: 'Country-specific Videra environments for NHS England, Scotland, Wales and Northern Ireland — project delivery, lifecycle, evidence and governance.',
    path: '/videra/healthcare',
  });

  return (
    <div data-testid="v2-videra-healthcare">
      <ProductHero
        eyebrow="Videra Healthcare"
        title="Project and lifecycle delivery built around healthcare."
        body={(
          <>
            <p>Healthcare estates operate continuously. Projects therefore need to balance delivery with clinical requirements, governance, safety, operational disruption and long-term asset needs.</p>
            <p>Videra Healthcare connects capital and lifecycle requirements with the governance and project controls required to deliver them.</p>
          </>
        )}
        primary={{ label: 'Try Videra Healthcare — 24 days', href: 'https://demo.videra.app/explore', testid: 'videra-healthcare-try' }}
        secondary={{ label: 'Talk to Keystone', to: '/contact' }}
        heroImage="/images/nhs/nhs-portfolio-tracker.jpg"
        heroImageAlt="Videra Healthcare NHS Portfolio Tracker"
        testid="videra-healthcare-hero"
      />

      {/* Countries */}
      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Country platforms</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">One platform, tuned to each health service.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {COUNTRIES.map((c) => (
              <div key={c.name} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`healthcare-country-${c.name.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-base font-medium text-slate-900">{c.name}</p>
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded ${c.status === 'Available now' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{c.status}</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{c.body}</p>
                {c.cta.href ? (
                  <a href={c.cta.href} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
                    {c.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <Link to={c.cta.to} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
                    {c.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">The healthcare journey</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-10">Requirement to lifecycle, in one environment.</h2>
          <Workflow testid="healthcare-workflow" steps={['Requirement','Governance','Project','Delivery','Commissioning','Evidence','Lifecycle position']} />
        </div>
      </section>

      <ScreenshotGallery
        eyebrow="Inside Videra Healthcare"
        title="Real screens. Real workflows."
        description="A sample of the NHS-aligned environment approved investors and evaluating trusts see when they open a 24-day trial."
        shots={HEALTHCARE_SHOTS}
        testid="healthcare-gallery"
      />

      <TestimonialBlock product="videra-healthcare" />
    </div>
  );
};

export default V2VideraHealthcare;
